import React from "react";
import { AppIcon } from "../Generic/AppIcon";
import {
  CALENDAR_ICON,
  DOLLAR_PAPER,
  LOCATION_ICON,
  WARNING_TRIANGLE_ICON,
} from "../../utils/svgs_new";
import Flex from "../Generic/Flex";
import { AppButton } from "../Generic/AppButton";
import { AppAbstractModal } from "../Generic/AppModal/AppAbstractModal";
import styled from "@emotion/styled";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";

interface Props {
  visitDetails: React.ReactNode;
  address: string;
  isOpen: boolean;
  showVisitRate: boolean;
  visitRate: number;
  estimatedSalaryPerWeek: number;
  onClickRequest: () => void;
  onClickClose: () => void;
}

const BaseMedflytRequestVisitModal = (props: Props) => {
  return (
    <BaseRequestModalWrapper isOpen={props.isOpen} onRequestClose={props.onClickClose}>
      <ModalContent>
        <BaseRequestVisitHeader>
          {translate(STRINGS.ARE_YOU_SURE_YOU_WANT_TO_REQUEST_THIS_VISIT)}
        </BaseRequestVisitHeader>
        <DetailsRowContainer>
          <IconWrapper>
            <AppIcon icon={CALENDAR_ICON} />
          </IconWrapper>
          {props.visitDetails}
        </DetailsRowContainer>

        <DetailsRowContainer>
          <LocationIconWrapper>
            <AppIcon icon={LOCATION_ICON} fill="var(--primary-color)" />
          </LocationIconWrapper>
          <Flex direction="column" gap={8}>
            <div>{`${translate(STRINGS.ADDRESS_IS_NEAR)} ${props.address}`}</div>
            <HelperText>
              {translate(STRINGS.SLIDE_ADDRESS_EXACT_ADDRESS_AFTER_CONFIRMATION)}
            </HelperText>
          </Flex>
        </DetailsRowContainer>

        {props.showVisitRate ? (
          <DetailsRowContainer>
            <IconWrapper>
              <AppIcon icon={DOLLAR_PAPER} fill="var(--primary-color)" />
            </IconWrapper>
            <div>
              {translate(STRINGS.VISIT_RATE_REQUEST_CONFIRMATION)
                .replace("{VISIT_RATE}", props.visitRate.toString())
                .replace("{ESTIMATED_SALARY}", props.estimatedSalaryPerWeek.toString())}
            </div>
          </DetailsRowContainer>
        ) : null}
        <WarningMessageContainer>
          <WarningMessageContent>
            <IconWrapper>
              <AppIcon icon={WARNING_TRIANGLE_ICON} fill="var(--warning-color)" />
            </IconWrapper>
            <div>{translate(STRINGS.VISIT_REQUEST_CONFIRMATION_WARNING)}</div>
          </WarningMessageContent>
        </WarningMessageContainer>
        <ActionButtonsContainer>
          <AppButton color="primary" onClick={props.onClickRequest}>
            {translate(STRINGS.REQUEST_VISIT)}
          </AppButton>
          <AppButton variant="link" onClick={props.onClickClose}>
            {translate(STRINGS.I_HAVE_CHANGED_MY_MIND)}
          </AppButton>
        </ActionButtonsContainer>
      </ModalContent>
    </BaseRequestModalWrapper>
  );
};

export default BaseMedflytRequestVisitModal;

const BaseRequestModalWrapper = styled(AppAbstractModal)`
  .container {
    background-color: white;
    border-radius: 24px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const ModalContent = styled.div`
  margin: 10px 20px 10px 20px;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  justify-content: space-between;
  gap: 20px;
`;

const HelperText = styled.div`
  font-size: 12px;
  line-height: 1.33;
  color: var(--tw-gray-500);
`;

const DetailsRowContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const WarningMessageContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const WarningMessageContainer = styled.div`
  border-radius: 10px;
  background-color: var(--tw-orange-100);
  border: 1px solid var(--tw-orange-200);
  padding: 10px;
  font-weight: 500;
  font-size: 12x;
`;

const BaseRequestVisitHeader = styled.div`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.33;
  color: black;
  margin: 10px 32px 10px 32px;
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 15%;
  svg {
    min-width: 20px;
    height: 20px;
  }
`;

const LocationIconWrapper = styled.div`
  width: 20%;
  svg {
    min-width: 20px;
    height: 20px;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
